/**
 * Loads AdSense Custom Search Ads for the Web per
 * https://developers.google.com/custom-search-ads/web
 */

import { getRaasContext } from "./context";
import { sdkLog } from "./log";

export const GOOGLE_CSA_SCRIPT_URL =
  "https://www.google.com/adsense/search/ads.js";

let loadPromise: Promise<void> | null = null;

/** Google's `_googCsa` queue stub. Safe to call before ads.js finishes loading. */
export const installGoogCsaStub = (win: Window = window): void => {
  const w = win as Window & { _googCsa?: unknown };
  if (w._googCsa) return;
  (function (g: Window, o: string) {
    const gw = g as unknown as Record<string, any>;
    gw[o] =
      gw[o] ||
      function () {
        (gw[o].q = gw[o].q || []).push(arguments);
      };
    gw[o].t = Date.now();
  })(win, "_googCsa");
};

const loadAdsJs = (): Promise<void> =>
  new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.async = true;
    s.src = GOOGLE_CSA_SCRIPT_URL;
    s.onload = () => {
      sdkLog("Google ads.js loaded");
      resolve();
    };
    s.onerror = () =>
      reject(
        new Error(
          `[Raas] Failed to load ${GOOGLE_CSA_SCRIPT_URL}. Check CSP and network.`,
        ),
      );
    document.head.appendChild(s);
  });

/**
 * Installs the `_googCsa` stub and ensures `ads.js` is on the page (injects once).
 * If a matching `ads.js` tag already exists, does not add a duplicate (queue still works).
 * Requires `init()` first (logging reads `verbose` from context).
 */
export const ensureGoogleCsaLoaded = (): Promise<void> => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(
      new Error("[Raas] Google CSA can only be loaded in a browser"),
    );
  }

  if (loadPromise) return loadPromise;

  getRaasContext(); // require init(); sdkLog reads verbose from context

  installGoogCsaStub();

  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${GOOGLE_CSA_SCRIPT_URL}"]`,
  );
  if (existing) {
    sdkLog("Google ads.js tag already present");
    loadPromise = Promise.resolve();
    return loadPromise;
  }

  loadPromise = loadAdsJs().catch((err) => {
    loadPromise = null;
    throw err;
  });

  return loadPromise;
};
